import { useEffect, useState, useCallback } from "react";
import { Eye, EyeOff, UserPlus, Trash2, Save, Shield } from "lucide-react";
import { api } from "../lib/api";

/**
 * Settings → Users management panel.
 * Visible to: owner / super_admin / admin / sales_manager.
 * Sales Managers cannot create or assign Owner/Admin roles.
 */
export default function UsersAdminPanel({ currentUser }) {
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const canManageHigh = ["owner", "super_admin", "dealer", "admin"].includes(currentUser?.role);
  const isOwner = ["owner", "super_admin", "dealer"].includes(currentUser?.role);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [r1, r2] = await Promise.all([
        api.get("/users/roles"),
        api.get("/users"),
      ]);
      setRoles(r1.data.roles || []);
      setUsers(r2.data.items || []);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const allowedRolesForCreate = roles.filter((r) => {
    if (currentUser?.role === "sales_manager") return !["owner", "admin"].includes(r.v);
    if (currentUser?.role === "admin") return r.v !== "owner";
    return true;
  });

  return (
    <div className="space-y-6" data-testid="users-admin-panel">
      <div className="flex items-center gap-3">
        <Shield size={20} className="text-[var(--ssc-blue)]" />
        <div>
          <h2 className="text-white font-black text-xl">Team Members</h2>
          <p className="text-white/60 text-xs mt-0.5">Create and manage logins for your dealership team.</p>
        </div>
      </div>

      {err && <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-300 text-sm" data-testid="users-error">{err}</div>}

      <CreateUserForm
        roles={allowedRolesForCreate}
        onCreated={() => load()}
      />

      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="users-list">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-silver)]">
            {loading ? "Loading…" : `${users.length} user${users.length === 1 ? "" : "s"}`}
          </div>
        </div>
        <div className="divide-y divide-white/5">
          {users.map((u) => (
            <UserRow
              key={u.id || u.username || u.email}
              user={u}
              roles={roles}
              currentUser={currentUser}
              canManageHigh={canManageHigh}
              isOwner={isOwner}
              onChanged={() => load()}
            />
          ))}
          {!loading && users.length === 0 && (
            <div className="px-4 py-10 text-center text-white/40 text-sm">No users yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateUserForm({ roles, onCreated }) {
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "", role: "salesperson", title: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setOk(""); setBusy(true);
    try {
      const { data } = await api.post("/users", {
        name: form.name.trim(),
        username: form.username.trim(),
        password: form.password,
        role: form.role,
        email: form.email.trim() || undefined,
        title: form.title.trim() || undefined,
      });
      setOk(`Created ${data.user.name} (${data.user.username}) — role: ${data.user.role}`);
      setForm({ name: "", username: "", email: "", password: "", role: "salesperson", title: "" });
      onCreated?.();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to create user.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3" data-testid="create-user-form">
      <div className="flex items-center gap-2 text-white font-black">
        <UserPlus size={16} className="text-[var(--ssc-blue)]" />
        <span>Add a Team Member</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FieldDark label="Full Name" testid="new-user-name">
          <input className="ssc-input-dark" value={form.name}
                 onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required
                 data-testid="new-user-name-input" placeholder="e.g. Jeremy F" />
        </FieldDark>
        <FieldDark label="Username" testid="new-user-username">
          <input className="ssc-input-dark" value={form.username}
                 onChange={(e) => setForm((f) => ({ ...f, username: e.target.value.replace(/\s+/g, "") }))} required
                 data-testid="new-user-username-input" placeholder="e.g. JeremyF" autoCapitalize="off" autoComplete="off" />
        </FieldDark>
        <FieldDark label="Email (optional)" testid="new-user-email">
          <input className="ssc-input-dark" type="email" value={form.email}
                 onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                 data-testid="new-user-email-input" placeholder="user@scottsellschevys.com" />
        </FieldDark>
        <FieldDark label="Title (optional)" testid="new-user-title">
          <input className="ssc-input-dark" value={form.title}
                 onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                 data-testid="new-user-title-input" placeholder="e.g. BDC Lead" />
        </FieldDark>
        <FieldDark label="Password" testid="new-user-password" full>
          <div className="relative">
            <input className="ssc-input-dark pr-10" type={showPw ? "text" : "password"}
                   value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} required
                   data-testid="new-user-password-input" autoComplete="new-password" />
            <button type="button" onClick={() => setShowPw((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 hover:text-white p-1.5"
                    aria-label={showPw ? "Hide password" : "Show password"}
                    data-testid="new-user-show-password">
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </FieldDark>
        <FieldDark label="Role" testid="new-user-role" full>
          <select className="ssc-input-dark" value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                  data-testid="new-user-role-select">
            {roles.map((r) => (
              <option key={r.v} value={r.v}>{r.label}</option>
            ))}
          </select>
        </FieldDark>
      </div>
      {err && <div className="text-sm text-red-300" data-testid="new-user-error">{err}</div>}
      {ok && <div className="text-sm text-green-300" data-testid="new-user-success">{ok}</div>}
      <button type="submit" disabled={busy} className="ssc-btn ssc-btn-primary text-sm" data-testid="new-user-submit">
        {busy ? "Creating…" : "Create User"}
      </button>
    </form>
  );
}

function UserRow({ user, roles, currentUser, canManageHigh, isOwner, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ role: user.role, active: user.active !== false, password: "", title: user.title || "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const rank = { owner: 100, super_admin: 100, dealer: 100, admin: 80, sales_manager: 60 };
  const targetRank = rank[user.role] ?? 40;
  const actorRank = rank[currentUser?.role] ?? 0;
  const canEdit = (actorRank >= targetRank) || currentUser?.id === user.id;
  const canDelete = isOwner ? user.id !== currentUser?.id
                            : (user.id !== currentUser?.id && targetRank < actorRank && !["owner", "admin"].includes(user.role));

  const allowedRoles = roles.filter((r) => {
    if (currentUser?.role === "sales_manager") return !["owner", "admin"].includes(r.v);
    if (currentUser?.role === "admin") return r.v !== "owner";
    return true;
  });

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { role: draft.role, active: draft.active, title: draft.title };
      if (draft.password) payload.password = draft.password;
      await api.patch(`/users/${user.id}`, payload);
      setEditing(false); setDraft((d) => ({ ...d, password: "" }));
      onChanged?.();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${user.name}? This cannot be undone.`)) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/users/${user.id}`);
      onChanged?.();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  const roleLabel = roles.find((r) => r.v === user.role)?.label || user.role;

  return (
    <div className="px-4 py-3" data-testid={`user-row-${user.username || user.email}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-white font-bold truncate">{user.name}</div>
          <div className="text-white/50 text-xs truncate">
            {user.username && <span className="font-mono">{user.username}</span>}
            {user.username && user.email && <span className="mx-1.5">·</span>}
            {user.email}
          </div>
          {user.title && <div className="text-white/40 text-[11px] mt-0.5">{user.title}</div>}
        </div>
        {!editing && (
          <div className="flex items-center gap-2 shrink-0">
            <span className="bg-[var(--ssc-blue)]/20 text-[var(--ssc-blue)] text-[10px] font-bold uppercase tracking-widest rounded-full px-2.5 py-1">
              {roleLabel}
            </span>
            {user.active === false && (
              <span className="bg-red-500/20 text-red-300 text-[10px] font-bold uppercase tracking-widest rounded-full px-2.5 py-1">Disabled</span>
            )}
            {canEdit && (
              <button onClick={() => setEditing(true)} className="text-xs text-white/60 hover:text-white px-2 py-1"
                      data-testid={`user-edit-${user.username || user.email}`}>Edit</button>
            )}
            {canDelete && (
              <button onClick={remove} disabled={busy} className="text-red-400 hover:text-red-300 p-1.5"
                      title="Delete user" aria-label="Delete user"
                      data-testid={`user-delete-${user.username || user.email}`}>
                <Trash2 size={14} />
              </button>
            )}
          </div>
        )}
      </div>

      {editing && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 bg-white/5 rounded-xl p-3">
          <FieldDark label="Role">
            <select className="ssc-input-dark" value={draft.role}
                    onChange={(e) => setDraft((d) => ({ ...d, role: e.target.value }))}
                    data-testid={`user-edit-role-${user.username || user.email}`}>
              {allowedRoles.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
          </FieldDark>
          <FieldDark label="Title">
            <input className="ssc-input-dark" value={draft.title}
                   onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} />
          </FieldDark>
          <FieldDark label="Reset Password (optional)" full>
            <div className="relative">
              <input className="ssc-input-dark pr-10" type={showPw ? "text" : "password"}
                     value={draft.password}
                     onChange={(e) => setDraft((d) => ({ ...d, password: e.target.value }))}
                     placeholder="Leave blank to keep current password"
                     autoComplete="new-password"
                     data-testid={`user-edit-password-${user.username || user.email}`} />
              <button type="button" onClick={() => setShowPw((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 hover:text-white p-1.5"
                      aria-label={showPw ? "Hide password" : "Show password"}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </FieldDark>
          <div className="flex items-center gap-2 mt-1">
            <input id={`active-${user.id}`} type="checkbox" checked={!!draft.active}
                   onChange={(e) => setDraft((d) => ({ ...d, active: e.target.checked }))}
                   className="rounded border-white/30 bg-white/5"
                   data-testid={`user-edit-active-${user.username || user.email}`} />
            <label htmlFor={`active-${user.id}`} className="text-white/80 text-sm">Account active</label>
          </div>
          {err && <div className="text-sm text-red-300 sm:col-span-2">{err}</div>}
          <div className="flex items-center gap-2 sm:col-span-2">
            <button onClick={save} disabled={busy} className="ssc-btn ssc-btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                    data-testid={`user-save-${user.username || user.email}`}>
              <Save size={13} /> {busy ? "Saving…" : "Save"}
            </button>
            <button onClick={() => { setEditing(false); setErr(""); }} className="text-xs text-white/60 hover:text-white px-3 py-1.5">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldDark({ label, children, testid, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""} data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-white/50 mb-1">{label}</div>
      {children}
    </div>
  );
}
